import { authFetch } from "@/lib/api-client";
import { API_REQUEST_TIMEOUT_UPLOAD_MS } from "@/lib/query-constants";
import { triggerBlobDownload } from "@/features/opportunities/lib/triggerBlobDownload";
import { parseContentDispositionFilename } from "@/features/marketplace/lib/parse-content-disposition-filename";
import type { MarketplaceOrderHandoffDefaults } from "@nexus/contracts";
import type {
  Vendor,
  TenantVendorConnection,
  CreateConnectionPayload,
  UpdateAnswersPayload,
  ResignConnectionPayload,
  MarketplaceOrder,
  TenantOrdersFilters,
  TenantOrdersPageResponse,
  SentScriptsMap,
  CreateOrderPayload,
  CreateOrderResult,
  OrderHandoffDefaultsPreview,
  OrderHandoffEmailPreview,
  OrderHandoffEmailPreviewPayload,
  UpdateOrderDueDatePayload,
  UpdateOrderDueDateResult,
  UpdateOrderEtaPayload,
  UpdateOrderEtaResult,
  MarketplaceFulfillmentStatus,
  AdminOrdersFilters,
  AdminOrdersPageResponse,
  AdminOrderVendorOption,
  DuplicatedOrderScript,
  OrderPartnerOption,
  SendOrderScriptToPartnerPayload,
  UpdateOrderScriptFulfillmentPayload,
  PartnerOrder,
  PartnerOrdersFilter,
  PartnerOrdersPageResponse,
  PartnerOrdersQueryFilters,
  PartnerPreflightIntent,
  EnablePartnerLoginResult,
  UnlinkPartnerLoginResult,
  CreateCustomBriefPayload,
  OrderScriptPreflightComment,
  MarketplaceOrderScriptByReviewLink,
  SetOrderNoteReactionPayload,
  SetOrderNoteReactionResult,
} from "@/features/marketplace/types/marketplace.types";

type RollingOrderScriptActivity = Omit<
  OrderScriptPreflightComment,
  "reactions"
> & {
  reactions?: OrderScriptPreflightComment["reactions"];
};

// ─── Reads ─────────────────────────────────────────────────────────────────

export async function fetchVendors(tenantId: string): Promise<Vendor[]> {
  const { data } = await authFetch<Vendor[]>(
    `/marketplace/vendors?tenantId=${encodeURIComponent(tenantId)}`,
  );
  return data;
}

/** Vendor detail (packages + questions) for the connect wizard. */
export async function fetchVendorDetails(vendorId: string): Promise<Vendor> {
  const { data } = await authFetch<Vendor>(
    `/marketplace/vendors/${encodeURIComponent(vendorId)}/questions`,
  );
  return data;
}

export async function fetchConnections(
  tenantId: string,
): Promise<TenantVendorConnection[]> {
  const { data } = await authFetch<TenantVendorConnection[]>(
    `/marketplace/connections?tenantId=${encodeURIComponent(tenantId)}`,
  );
  return data;
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export async function createConnection(
  payload: CreateConnectionPayload,
): Promise<TenantVendorConnection> {
  const { data } = await authFetch<TenantVendorConnection>(
    "/marketplace/connections",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function updateAnswers(
  connectionId: string,
  payload: UpdateAnswersPayload,
): Promise<TenantVendorConnection> {
  const { data } = await authFetch<TenantVendorConnection>(
    `/marketplace/connections/${encodeURIComponent(connectionId)}/answers`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function resignConnection(
  connectionId: string,
  payload: ResignConnectionPayload,
): Promise<TenantVendorConnection> {
  const { data } = await authFetch<TenantVendorConnection>(
    `/marketplace/connections/${encodeURIComponent(connectionId)}/resign`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function disconnectVendor(
  connectionId: string,
  tenantId: string,
): Promise<void> {
  await authFetch(
    `/marketplace/connections/${encodeURIComponent(connectionId)}`,
    {
      method: "DELETE",
      body: JSON.stringify({ tenantId }),
    },
  );
}

// ─── Orders ────────────────────────────────────────────────────────────────

export async function fetchOrders(
  tenantId: string,
  options?: TenantOrdersFilters,
): Promise<TenantOrdersPageResponse> {
  const params = new URLSearchParams({ tenantId });
  if (options?.hubSetScope) params.set("hubSetScope", "true");
  if (options?.opportunitySetIds?.length) {
    params.set("opportunitySetIds", options.opportunitySetIds.join(","));
  } else if (options?.opportunitySetId) {
    params.set("opportunitySetId", options.opportunitySetId);
  }
  if (options?.fulfillmentGroup) {
    params.set("fulfillmentGroup", options.fulfillmentGroup);
  }
  if (options?.lineType) params.set("lineType", options.lineType);
  if (options?.limit != null) params.set("limit", String(options.limit));
  if (options?.offset != null) params.set("offset", String(options.offset));
  const { data } = await authFetch<TenantOrdersPageResponse>(
    `/marketplace/orders?${params.toString()}`,
  );
  return data;
}

export async function fetchSentScripts(
  tenantId: string,
): Promise<SentScriptsMap> {
  const { data } = await authFetch<SentScriptsMap>(
    `/marketplace/orders/sent-scripts?tenantId=${encodeURIComponent(tenantId)}`,
  );
  return data;
}

export async function fetchOrderHandoffDefaults(
  tenantId: string,
  tenantVendorId?: string,
): Promise<OrderHandoffDefaultsPreview> {
  const params = new URLSearchParams({ tenantId });
  if (tenantVendorId) params.set("tenantVendorId", tenantVendorId);
  const { data } = await authFetch<
    OrderHandoffDefaultsPreview | MarketplaceOrderHandoffDefaults
  >(`/marketplace/order-handoff-defaults?${params.toString()}`);
  if (data && typeof data === "object" && "defaults" in data && "recipients" in data) {
    return data;
  }
  return {
    defaults: data,
    recipients: { to: [], cc: [] },
  };
}

export async function fetchOrder(
  tenantId: string,
  orderId: string,
  fulfillmentGroup?: "active" | "history" | "archive",
): Promise<MarketplaceOrder> {
  const params = new URLSearchParams({ tenantId });
  if (fulfillmentGroup) params.set("fulfillmentGroup", fulfillmentGroup);
  const { data } = await authFetch<MarketplaceOrder>(
    `/marketplace/orders/${encodeURIComponent(orderId)}?${params.toString()}`,
  );
  return data;
}

export async function previewOrderHandoffEmail(
  payload: OrderHandoffEmailPreviewPayload,
): Promise<OrderHandoffEmailPreview> {
  const { data } = await authFetch<OrderHandoffEmailPreview>(
    "/marketplace/order-handoff-email-preview",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  )
  return data
}

/** Stable across retries of the same tenant + connection + script set. */
export function marketplaceOrderCreateIdempotencyKey(
  payload: Pick<CreateOrderPayload, "tenantId" | "tenantVendorId" | "scriptIds" | "briefIds">,
): string {
  const scriptPart = [...payload.scriptIds].sort().join(",");
  const briefPart = [...(payload.briefIds ?? [])].sort().join(",");
  return `order-create:${payload.tenantId}:${payload.tenantVendorId}:${scriptPart}:${briefPart}`;
}

export async function createOrder(
  payload: CreateOrderPayload,
): Promise<CreateOrderResult> {
  const idempotencyKey = marketplaceOrderCreateIdempotencyKey(payload);
  const res = await authFetch("/marketplace/orders", {
    method: "POST",
    body: JSON.stringify({ ...payload, idempotencyKey }),
    rawResponse: true,
  });
  const json = (await res.json()) as {
    alreadySent?: boolean;
    orderId?: string | null;
    status?: MarketplaceOrder["status"] | null;
    data: MarketplaceOrder | null;
  };
  return {
    alreadySent: json.alreadySent === true,
    orderId: json.orderId ?? json.data?.id ?? null,
    status: json.status ?? json.data?.status ?? null,
    data: json.data,
  };
}

export interface RetryOrderResult {
  orderId: string;
  status: MarketplaceOrder["status"];
}

export async function retryOrder(
  orderId: string,
  tenantId: string,
): Promise<RetryOrderResult> {
  const { data } = await authFetch<RetryOrderResult>(
    `/marketplace/orders/${encodeURIComponent(orderId)}/retry`,
    {
      method: "POST",
      body: JSON.stringify({ tenantId }),
    },
  );
  return data;
}

export async function updateOrderEta(
  orderId: string,
  payload: UpdateOrderEtaPayload,
): Promise<UpdateOrderEtaResult> {
  const res = await authFetch(
    `/marketplace/orders/${encodeURIComponent(orderId)}/eta`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
      rawResponse: true,
    },
  );
  const json = (await res.json()) as {
    success: boolean;
    data: MarketplaceOrder;
    emailWarning?: string;
  };
  return { order: json.data, emailWarning: json.emailWarning };
}

export async function updateOrderDueDate(
  orderId: string,
  payload: UpdateOrderDueDatePayload,
): Promise<UpdateOrderDueDateResult> {
  const res = await authFetch(
    `/marketplace/orders/${encodeURIComponent(orderId)}/due-date`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
      rawResponse: true,
    },
  );
  const json = (await res.json()) as {
    success: boolean;
    data: MarketplaceOrder;
    emailWarning?: string;
  };
  return { order: json.data, emailWarning: json.emailWarning };
}

export async function updateOrderFulfillment(
  orderId: string,
  tenantId: string,
  fulfillmentStatus: MarketplaceFulfillmentStatus,
): Promise<MarketplaceOrder> {
  const { data } = await authFetch<MarketplaceOrder>(
    `/marketplace/orders/${encodeURIComponent(orderId)}/fulfillment-status`,
    {
      method: "PATCH",
      body: JSON.stringify({ tenantId, fulfillmentStatus }),
    },
  );
  return data;
}

// ─── Ops & partner orders ───────────────────────────────────────────────────

export async function fetchAdminOrderVendors(): Promise<AdminOrderVendorOption[]> {
  const { data } = await authFetch<AdminOrderVendorOption[]>(
    "/marketplace/admin/orders/vendors",
  );
  return data;
}

export async function fetchAdminOrders(
  filters: AdminOrdersFilters = {},
): Promise<AdminOrdersPageResponse> {
  const params = new URLSearchParams();
  if (filters.clientTenantId) params.set("clientTenantId", filters.clientTenantId);
  if (filters.clientTenantIds?.length) {
    params.set("clientTenantIds", filters.clientTenantIds.join(","));
  }
  if (filters.vendorId) params.set("vendorId", filters.vendorId);
  if (filters.vendorIds?.length) {
    params.set("vendorIds", filters.vendorIds.join(","));
  }
  if (filters.orderId) params.set("orderId", filters.orderId);
  if (filters.statuses?.length) params.set("statuses", filters.statuses.join(","));
  if (filters.dueFrom) params.set("dueFrom", filters.dueFrom);
  if (filters.dueTo) params.set("dueTo", filters.dueTo);
  if (filters.overdueOnly) params.set("overdueOnly", "true");
  if (filters.attentionOnly) params.set("attentionOnly", "true");
  if (filters.deadlineBucket) {
    params.set("deadlineBucket", filters.deadlineBucket);
  }
  if (filters.fulfillmentGroup) {
    params.set("fulfillmentGroup", filters.fulfillmentGroup);
  }
  if (filters.lineType) params.set("lineType", filters.lineType);
  if (filters.createdFrom) params.set("createdFrom", filters.createdFrom);
  if (filters.createdTo) params.set("createdTo", filters.createdTo);
  if (filters.deliveredFrom) {
    params.set("deliveredFrom", filters.deliveredFrom);
  }
  if (filters.deliveredTo) params.set("deliveredTo", filters.deliveredTo);
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortDirection) params.set("sortDirection", filters.sortDirection);
  if (filters.limit != null) params.set("limit", String(filters.limit));
  if (filters.offset != null) params.set("offset", String(filters.offset));
  if (filters.includeSummary) params.set("includeSummary", "true");
  const query = params.toString();
  const { data } = await authFetch<AdminOrdersPageResponse>(
    `/marketplace/admin/orders${query ? `?${query}` : ""}`,
  );
  return data;
}

export type UpdateOrderScriptVideoLabelResult = {
  id: string;
  label: string | null;
};

export async function updateOrderScriptVideoLabel(
  orderId: string,
  orderScriptId: string,
  deliverableId: string,
  label: string | null,
  options?: { admin?: boolean },
): Promise<UpdateOrderScriptVideoLabelResult> {
  const prefix = options?.admin ? "/marketplace/admin" : "/marketplace/partner";
  const { data } = await authFetch<UpdateOrderScriptVideoLabelResult>(
    `${prefix}/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/videos/${encodeURIComponent(deliverableId)}/label`,
    {
      method: "PATCH",
      body: JSON.stringify({ label }),
    },
  );
  return data;
}

export async function updateOrderScriptFulfillment(
  orderId: string,
  orderScriptId: string,
  payload: UpdateOrderScriptFulfillmentPayload,
  options?: { admin?: boolean },
): Promise<MarketplaceOrder> {
  const prefix = options?.admin ? "/marketplace/admin" : "/marketplace";
  const { data } = await authFetch<MarketplaceOrder>(
    `${prefix}/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/fulfillment-status`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function duplicateOrderScript(
  orderId: string,
  orderScriptId: string,
  idempotencyKey: string,
): Promise<DuplicatedOrderScript> {
  const { data } = await authFetch<DuplicatedOrderScript>(
    `/marketplace/admin/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/duplicate-script`,
    {
      method: "POST",
      body: JSON.stringify({ idempotencyKey }),
    },
  );
  return data;
}

export async function fetchAdminOrderPartnerOptions(
  orderId: string,
): Promise<OrderPartnerOption[]> {
  const { data } = await authFetch<OrderPartnerOption[]>(
    `/marketplace/admin/orders/${encodeURIComponent(orderId)}/partner-options`,
  );
  return data;
}

export async function sendOrderScriptToPartner(
  orderId: string,
  orderScriptId: string,
  payload: SendOrderScriptToPartnerPayload,
): Promise<CreateOrderResult> {
  const res = await authFetch(
    `/marketplace/admin/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/send-to-partner`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      rawResponse: true,
    },
  );
  const json = (await res.json()) as {
    alreadySent?: boolean;
    orderId?: string | null;
    status?: MarketplaceOrder["status"] | null;
    data: MarketplaceOrder | null;
  };
  return {
    alreadySent: json.alreadySent === true,
    orderId: json.orderId ?? json.data?.id ?? null,
    status: json.status ?? json.data?.status ?? null,
    data: json.data,
  };
}

export async function fetchPartnerOrder(
  partnerTenantId: string,
  orderId: string,
): Promise<PartnerOrder> {
  const { data } = await authFetch<PartnerOrder>(
    `/marketplace/partner/orders/${encodeURIComponent(orderId)}`,
    { headers: { "X-Tenant-Id": partnerTenantId } },
  );
  return data;
}

export async function fetchPartnerOrders(
  partnerTenantId: string,
  filters: PartnerOrdersQueryFilters = { filter: "all" },
): Promise<PartnerOrdersPageResponse> {
  const params = new URLSearchParams({ filter: filters.filter });
  if (filters.lineType) params.set("lineType", filters.lineType);
  const trimmedOrderId = filters.orderId?.trim();
  if (trimmedOrderId) {
    params.set("orderId", trimmedOrderId);
  }
  if (filters.limit != null) params.set("limit", String(filters.limit));
  if (filters.offset != null) params.set("offset", String(filters.offset));
  const { data } = await authFetch<PartnerOrdersPageResponse>(
    `/marketplace/partner/orders?${params.toString()}`,
    { headers: { "X-Tenant-Id": partnerTenantId } },
  );
  return data;
}

/** One key per logical add or replace, so a retry reconciles the same submission. */
export function partnerPreflightIdempotencyKey(input: {
  orderId: string;
  orderScriptId: string;
  uploadId: string;
  intent?: PartnerPreflightIntent;
}): string {
  if (!input.intent) return crypto.randomUUID();
  if ("deliverableId" in input.intent) {
    return `replace:${input.orderId}:${input.orderScriptId}:${input.intent.deliverableId}:${input.uploadId}`;
  }
  return `add:${input.orderId}:${input.orderScriptId}:${input.uploadId}`;
}

export async function uploadPartnerPreflight(
  partnerTenantId: string,
  orderId: string,
  orderScriptId: string,
  uploadId: string,
  options?: {
    intent?: PartnerPreflightIntent;
    /** Names an alternate hook; omitted, the video is the primary's. */
    hookKey?: string;
  },
): Promise<MarketplaceOrder> {
  const { data } = await authFetch<MarketplaceOrder>(
    `/marketplace/partner/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/preflight`,
    {
      method: "POST",
      headers: { "X-Tenant-Id": partnerTenantId },
      body: JSON.stringify({
        uploadId,
        idempotencyKey: partnerPreflightIdempotencyKey({
          orderId,
          orderScriptId,
          uploadId,
          ...(options?.intent ? { intent: options.intent } : {}),
        }),
        ...(options?.intent ?? {}),
        ...(options?.hookKey ? { hookKey: options.hookKey } : {}),
      }),
    },
  );
  return data;
}

export type PartnerVoiceoverCheckStart =
  | { checkable: false }
  | { checkable: true; correlationId: string };

export async function startPartnerVoiceoverCheck(
  partnerTenantId: string,
  orderId: string,
  orderScriptId: string,
  uploadId: string,
  /** Names an alternate hook; omitted, the video is checked against the primary. */
  hookKey?: string,
): Promise<PartnerVoiceoverCheckStart> {
  // The 202 carries correlationId at the top level; the not-checkable 200 carries `data`.
  const { data, correlationId } = await authFetch<{ checkable: false } | undefined>(
    `/marketplace/partner/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/voiceover-check`,
    {
      method: "POST",
      headers: { "X-Tenant-Id": partnerTenantId },
      // Same video + same order line + same hook = the same question; no need to re-ask.
      body: JSON.stringify(
        hookKey
          ? { uploadId, idempotencyKey: `${uploadId}:${orderScriptId}:${hookKey}`, hookKey }
          : { uploadId, idempotencyKey: `${uploadId}:${orderScriptId}` },
      ),
    },
  );
  if (correlationId) return { checkable: true, correlationId };
  if (data?.checkable === false) return { checkable: false };
  throw new Error("Voiceover check response missing correlationId");
}

export async function downloadPartnerOrderScriptAsset(
  partnerTenantId: string,
  orderId: string,
  orderScriptId: string,
): Promise<void> {
  const { data } = await authFetch<{ downloadUrl: string; originalName: string | null }>(
    `/marketplace/partner/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/download`,
    { headers: { "X-Tenant-Id": partnerTenantId } },
  );
  triggerSignedUrlDownload(data.downloadUrl, data.originalName);
}

export async function downloadOrderScriptAsset(
  orderId: string,
  orderScriptId: string,
  tenantId: string,
): Promise<void> {
  const { data } = await authFetch<{ downloadUrl: string; originalName: string | null }>(
    `/marketplace/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/download?tenantId=${encodeURIComponent(tenantId)}`,
  );
  triggerSignedUrlDownload(data.downloadUrl, data.originalName);
}

function triggerSignedUrlDownload(downloadUrl: string, originalName: string | null): void {
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = originalName ?? "download";
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

export async function fetchOrderScriptPreflightComments(
  orderId: string,
  orderScriptId: string,
): Promise<OrderScriptPreflightComment[]> {
  const { data } = await authFetch<RollingOrderScriptActivity[]>(
    `/marketplace/admin/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/preflight-comments`,
  );
  return normalizeOrderScriptActivity(data);
}

export async function fetchTenantOrderScriptPreflightComments(
  orderId: string,
  orderScriptId: string,
  tenantId: string,
): Promise<OrderScriptPreflightComment[]> {
  const { data } = await authFetch<RollingOrderScriptActivity[]>(
    `/marketplace/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/preflight-comments?tenantId=${encodeURIComponent(tenantId)}`,
  );
  return normalizeOrderScriptActivity(data);
}

export async function fetchPartnerOrderScriptPreflightComments(
  partnerTenantId: string,
  orderId: string,
  orderScriptId: string,
): Promise<OrderScriptPreflightComment[]> {
  const { data } = await authFetch<RollingOrderScriptActivity[]>(
    `/marketplace/partner/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/preflight-comments`,
    { headers: { "X-Tenant-Id": partnerTenantId } },
  );
  return normalizeOrderScriptActivity(data);
}

function normalizeOrderScriptActivity(
  entries: RollingOrderScriptActivity[],
): OrderScriptPreflightComment[] {
  return entries.map((entry) => ({
    ...entry,
    reactions: entry.reactions ?? [],
  }));
}

export async function setAdminOrderNoteReaction(
  payload: SetOrderNoteReactionPayload,
): Promise<SetOrderNoteReactionResult> {
  const { data } = await authFetch<SetOrderNoteReactionResult>(
    `/marketplace/admin/orders/${encodeURIComponent(payload.orderId)}/scripts/${encodeURIComponent(payload.orderScriptId)}/preflight-comments/${encodeURIComponent(payload.commentId)}/reactions`,
    {
      method: "PUT",
      body: JSON.stringify({
        reactionType: payload.reactionType,
        active: payload.active,
        idempotencyKey: payload.idempotencyKey,
      }),
    },
  );
  return data;
}

export async function setClientOrderNoteReaction(
  payload: SetOrderNoteReactionPayload,
): Promise<SetOrderNoteReactionResult> {
  const { data } = await authFetch<SetOrderNoteReactionResult>(
    `/marketplace/orders/${encodeURIComponent(payload.orderId)}/scripts/${encodeURIComponent(payload.orderScriptId)}/preflight-comments/${encodeURIComponent(payload.commentId)}/reactions`,
    {
      method: "PUT",
      body: JSON.stringify({
        tenantId: payload.tenantId,
        reactionType: payload.reactionType,
        active: payload.active,
        idempotencyKey: payload.idempotencyKey,
      }),
    },
  );
  return data;
}

export async function setPartnerOrderNoteReaction(
  payload: SetOrderNoteReactionPayload,
): Promise<SetOrderNoteReactionResult> {
  const { data } = await authFetch<SetOrderNoteReactionResult>(
    `/marketplace/partner/orders/${encodeURIComponent(payload.orderId)}/scripts/${encodeURIComponent(payload.orderScriptId)}/preflight-comments/${encodeURIComponent(payload.commentId)}/reactions`,
    {
      method: "PUT",
      headers: { "X-Tenant-Id": payload.tenantId },
      body: JSON.stringify({
        reactionType: payload.reactionType,
        active: payload.active,
        idempotencyKey: payload.idempotencyKey,
      }),
    },
  );
  return data;
}

export async function fetchMarketplaceOrderScriptByScriptReviewId(
  scriptReviewId: string,
): Promise<MarketplaceOrderScriptByReviewLink> {
  const { data } = await authFetch<MarketplaceOrderScriptByReviewLink>(
    `/marketplace/admin/order-scripts/by-script-review/${encodeURIComponent(scriptReviewId)}`,
  );
  return data;
}

export async function addPartnerOrderScriptNote(
  partnerTenantId: string,
  orderId: string,
  orderScriptId: string,
  payload: { body: string; deliverableId?: string; hookKey?: string },
): Promise<{ comment: OrderScriptPreflightComment; order: PartnerOrder }> {
  const { data } = await authFetch<{
    comment: OrderScriptPreflightComment;
    order: PartnerOrder;
  }>(
    `/marketplace/partner/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/preflight-comments`,
    {
      method: "POST",
      headers: { "X-Tenant-Id": partnerTenantId },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function addOrderScriptPreflightComment(
  orderId: string,
  orderScriptId: string,
  payload: {
    tenantId: string;
    body: string;
    requestRevision?: boolean;
    deliverableId?: string;
  },
): Promise<{ comment: OrderScriptPreflightComment; order: MarketplaceOrder }> {
  const { data } = await authFetch<{ comment: OrderScriptPreflightComment; order: MarketplaceOrder }>(
    `/marketplace/admin/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/preflight-comments`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function addTenantOrderScriptPreflightComment(
  orderId: string,
  orderScriptId: string,
  payload: {
    tenantId: string;
    body: string;
    requestRevision?: boolean;
    deliverableId?: string;
  },
): Promise<{ comment: OrderScriptPreflightComment; order: MarketplaceOrder }> {
  const { data } = await authFetch<{ comment: OrderScriptPreflightComment; order: MarketplaceOrder }>(
    `/marketplace/orders/${encodeURIComponent(orderId)}/scripts/${encodeURIComponent(orderScriptId)}/preflight-comments`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function enablePartnerLogin(vendorId: string): Promise<EnablePartnerLoginResult> {
  const { data } = await authFetch<EnablePartnerLoginResult>(
    `/marketplace/admin/vendors/${encodeURIComponent(vendorId)}/enable-partner-login`,
    { method: "POST" },
  );
  return data;
}

export async function unlinkPartnerLogin(vendorId: string): Promise<UnlinkPartnerLoginResult> {
  const { data } = await authFetch<UnlinkPartnerLoginResult>(
    `/marketplace/admin/vendors/${encodeURIComponent(vendorId)}/unlink-partner-login`,
    { method: "POST" },
  );
  return data;
}

export async function createOpportunityCustomBrief(
  setId: string,
  payload: CreateCustomBriefPayload,
): Promise<{ id: string }> {
  const { data } = await authFetch<{ id: string }>(
    `/opportunity-sets/${encodeURIComponent(setId)}/scripts/custom-brief`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function downloadScriptCustomAsset(
  scope: "opportunities" | "persona-opportunities",
  setId: string,
  scriptId: string,
  tenantId: string,
): Promise<void> {
  const path =
    scope === "persona-opportunities"
      ? `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/custom-asset/download`
      : `/opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/custom-asset/download`;
  const response = await authFetch(`${path}?tenantId=${encodeURIComponent(tenantId)}`, {
    rawResponse: true,
    timeoutMs: API_REQUEST_TIMEOUT_UPLOAD_MS,
  });
  const blob = await response.blob();
  const filename =
    parseContentDispositionFilename(response.headers.get("Content-Disposition")) ?? "download";
  triggerBlobDownload(blob, filename);
}
